import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableWrapper, CriticalRow, RegularRow, NumberOfRows } from './styles';
import Patient from '../../../../../../Models/Patient';
import CompWellnessPatient from '../../../../../../Models/CompWellnessPatient';
import { IsOutstandingLumiPatientCritical, IsOutstandingCompWellnessPatientCritical } from '../../../../utils';
import { AppModeConfig } from "../../../../../../AppModes/AppModeConfig";
import { AppModes } from '../../../../../../Models/Constants/AppModes';

interface OutstandingPatientsTableProps {
    outstandingLumiPatients: Patient[];
    outstandingCompWellnessPatients: CompWellnessPatient[];
    appModeConfig: AppModeConfig;
}

export const OutstandingPatientsTable = ({ outstandingLumiPatients, outstandingCompWellnessPatients, appModeConfig }: OutstandingPatientsTableProps) => {

    const tableHeaders = [
        (appModeConfig.name === AppModes.COMPREHENSIVEWELLNESS) ? "GRC number" : "Sample ID",
        "Sample name",
        "Date received",
        (appModeConfig.name === AppModes.COMPREHENSIVEWELLNESS) ? "Kit number" : "",
        "Created by"
    ];

    const DetermineCriticalOutstandingLumiPatients = (outstandingPatients: Patient[]): [criticalPatients: Patient[], regularOutstandingPatients: Patient[]] => {
        var regular: Patient[] = [];
        var critical: Patient[] = [];

        outstandingPatients.forEach(lumiPatient => {
            if (IsOutstandingLumiPatientCritical(lumiPatient)) {
                critical.push(lumiPatient);
            } else {
                regular.push(lumiPatient);
            }
        });

        return [ critical, regular ];
    }

    const DetermineCriticalOutstandingCompWellnessPatients = (outstandingPatients: CompWellnessPatient[]): [criticalPatients: CompWellnessPatient[], regularOutstandingPatients: CompWellnessPatient[]] => {
        var regular: CompWellnessPatient[] = [];
        var critical: CompWellnessPatient[] = [];

        outstandingPatients.forEach(compWellnessPatient => {
            if (IsOutstandingCompWellnessPatientCritical(compWellnessPatient)) {
                critical.push(compWellnessPatient);
            } else {
                regular.push(compWellnessPatient);
            }
        });

        return [ critical, regular ];
    }

    const GenerateLumiTableRows = (outstandingLumiPatients: Patient[]): JSX.Element[] => {
        var [critical, regular] = DetermineCriticalOutstandingLumiPatients(outstandingLumiPatients)
    
        var rows: JSX.Element[] = [];

        critical.sort(LumiSortByDateCreated);
        regular.sort(LumiSortByDateCreated);

        critical.forEach((critical, index) => {
            var dateCreated = new Date(critical.dateCreated);
            rows.push (
                <CriticalRow key={`critical-row-${index}`}>
                    <TableCell>{critical.sampleId}</TableCell>
                    <TableCell>{`${critical.firstName} ${critical.lastName}`}</TableCell>
                    <TableCell>{`${dateCreated.toLocaleDateString('en-AU')} ${dateCreated.toLocaleTimeString('en-AU')}`}</TableCell>
                    <TableCell>{critical.createdBy}</TableCell>
                </CriticalRow>
            )
        });

        regular.forEach((regular, index) => {
            var dateCreated = new Date(regular.dateCreated);
            rows.push(
                <RegularRow key={`regular-row-${index}`}>
                    <TableCell>{regular.sampleId}</TableCell>
                    <TableCell>{`${regular.firstName} ${regular.lastName}`}</TableCell>
                    <TableCell>{`${dateCreated.toLocaleDateString('en-AU')} ${dateCreated.toLocaleTimeString('en-AU')}`}</TableCell>
                    <TableCell>{regular.createdBy}</TableCell>
                </RegularRow>
            );
        });

        return rows;
    }

    const GenerateCompWellnessTableRows = (outstandingCompWellnessPatients: CompWellnessPatient[]): JSX.Element[] => {
        var [critical, regular] = DetermineCriticalOutstandingCompWellnessPatients(outstandingCompWellnessPatients)
    
        var rows: JSX.Element[] = [];

        critical.sort(CompWellnessSortByDateCreated);
        regular.sort(CompWellnessSortByDateCreated);

        critical.forEach((critical, index) => {
            var dateCreated = new Date(critical.dateCreated);
            rows.push (
                <CriticalRow key={`critical-row-${index}`}>
                    <TableCell>{critical.grcNumber}</TableCell>
                    <TableCell>{`${critical.firstName} ${critical.lastName}`}</TableCell>
                    <TableCell>{`${dateCreated.toLocaleDateString('en-AU')} ${dateCreated.toLocaleTimeString('en-AU')}`}</TableCell>
                    <TableCell>{critical.kitNumber}</TableCell>
                    <TableCell>{critical.createdBy}</TableCell>
                </CriticalRow>
            )
        });

        regular.forEach((regular, index) => {
            var dateCreated = new Date(regular.dateCreated);
            rows.push(
                <RegularRow key={`regular-row-${index}`}>
                    <TableCell>{regular.grcNumber}</TableCell>
                    <TableCell>{`${regular.firstName} ${regular.lastName}`}</TableCell>
                    <TableCell>{`${dateCreated.toLocaleDateString('en-AU')} ${dateCreated.toLocaleTimeString('en-AU')}`}</TableCell>
                    <TableCell>{regular.kitNumber}</TableCell>
                    <TableCell>{regular.createdBy}</TableCell>
                </RegularRow>
            );
        });

        return rows;
    }

    const LumiSortByDateCreated = (a: Patient, b: Patient): number => {
        if (a.dateCreated < b.dateCreated) return -1; // a should be first
        if (a.dateCreated === b.dateCreated) return 0; // both the same
        return 1; // b should be first
    };

    const CompWellnessSortByDateCreated = (a: CompWellnessPatient, b: CompWellnessPatient): number => {
        if (a.dateCreated < b.dateCreated) return -1; // a should be first
        if (a.dateCreated === b.dateCreated) return 0; // both the same
        return 1; // b should be first
    };

    const rows = (appModeConfig.name === AppModes.COMPREHENSIVEWELLNESS) ? GenerateCompWellnessTableRows(outstandingCompWellnessPatients) : GenerateLumiTableRows(outstandingLumiPatients);

    return (
        <TableWrapper>
            <NumberOfRows>
                {`Number of rows: ${rows.length}`}
            </NumberOfRows>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                    <TableRow>
                        {tableHeaders.map((header, index) => <TableCell key={`header-${index}`}>{header}</TableCell>)}
                    </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows}
                    </TableBody>
                </Table>
            </TableContainer>
        </TableWrapper>
    );

}


